import React, { useState, useEffect, useRef } from "react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase.config";
import toast from "react-hot-toast";
import { Check, ArrowRight, Info, ShieldCheck } from "lucide-react";
import getTimezoneInfo from "../../utils/timezone";

const CoachDisclaimer = () => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  useEffect(() => {
    setInitialized(true);

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleCheckboxChange = (e) => {
    setAccepted(e.target.checked);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const currentUser = auth.currentUser;

      console.log("current user", currentUser.emailVerified);
      const emailVerification = currentUser.emailVerified;

      if (!currentUser) {
        console.error("No user is signed in");
        toast.error("Please sign in to continue");
        navigate('/login');
        return;
      }
      if (!emailVerification) {
        console.error("email not verified");
        toast.loading("Please verify your email to continue");
        navigate('/verify-email');
        return;
      }

      const userRef = doc(db, "users", currentUser.uid);
      const docSnap = await getDoc(userRef);
      console.log("doc snap", docSnap);

      if (!docSnap.exists()) {
        console.error("User document not found");
        alert("Account setup incomplete. Please contact support.");
        return;
      }

      const tzInfo = getTimezoneInfo();
      await updateDoc(userRef, {
        accept_terms: true,
        updatedAt: new Date(),
        ...tzInfo,
      });
      console.log("Terms accepted successfully");
      toast.success("Terms accepted. Welcome!");
      navigate("/profile");
    } catch (error) {
      console.error("Error updating terms acceptance:", error);
      if (mountedRef.current) {
        alert("Failed to update terms. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-50" style={{
        background: 'radial-gradient(circle at top, #111827 0, #020617 55%)'
      }}>
        Loading....
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-50" style={{
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
      background: 'radial-gradient(circle at top, #111827 0, #020617 55%)',
      WebkitFontSmoothing: 'antialiased',
      lineHeight: 1.7
    }}>
      <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-40 py-10 sm:py-12 md:py-16 lg:py-20">
        <div className="border shadow-2xl overflow-hidden" style={{
          borderRadius: '24px',
          background: 'radial-gradient(circle at top left, #1f2937, #020617 60%)',
          borderColor: 'rgba(148, 163, 184, 0.24)',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.7)',
          position: 'relative'
        }}>
          {/* Gradient overlay effect */}
          <div className="absolute pointer-events-none" style={{
            inset: '-40%',
            opacity: 0.9,
            background: 'radial-gradient(circle at 10% 0, rgba(248, 250, 252, 0.06), transparent 60%), radial-gradient(circle at 80% 0, rgba(249, 115, 22, 0.25), transparent 55%)'
          }}></div>

          {/* Header */}
          <div className="relative z-10 text-center" style={{ padding: '32px 20px 24px' }}>
            <h1 className="font-bold mb-2" style={{
              fontSize: '2rem',
              letterSpacing: '0.01em'
            }}>Disclaimer</h1>
            <p style={{ color: '#9ca3af', fontSize: '1.05rem' }}>Your journey to Ascendra starts here</p>
          </div>

          {/* Content */}
          <div className="relative z-10" style={{ padding: '0 20px 32px' }}>
            {/* Introduction */}
            <div className="border mb-5" style={{
              padding: '20px',
              borderRadius: '14px',
              background: 'rgba(15, 23, 42, 0.85)',
              borderColor: 'rgba(148, 163, 184, 0.35)'
            }}>
              <p style={{ marginBottom: '10px', color: '#e5e7eb', fontSize: '0.98rem', lineHeight: 1.7 }}>
                Ascendra is a deep-immersion, AI-empowered coaching application developed and owned by Ascendra LLC.
                It offers educational insights, structured check-ins, and real-time support intended to facilitate
                psychological shifts and sustainable behavioral change. Its purpose is to help users cultivate neutrality,
                balance, and self-regulation in their relationship with food and eating.
              </p>

              {/* What It Is Used For */}
              <div className="flex items-start gap-3 mb-4" style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}>
                <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#60a5fa' }} />
                <div className="space-y-2">
                  <p className="font-semibold" style={{ color: '#e5e7eb', fontSize: '0.95rem' }}>❗ What It Is Used For:</p>
                  <ul className="list-none space-y-1.5" style={{ paddingLeft: 0 }}>
                    {[
                      'Helping users rewire eating habits and food psychology',
                      'Providing mindset-based tools and motivational support',
                      'Reinforcing positive behaviors and daily victories',
                      'Guiding food decisions in real-life scenarios (e.g., cravings, restaurants, travel)'
                    ].map((item, i) => (
                      <li key={i} className="relative" style={{ paddingLeft: '18px', fontSize: '0.92rem', color: 'white' }}>
                        <span className="absolute" style={{ left: '4px', top: 0, color: '#60a5fa', fontSize: '0.8rem' }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* What It Is Not Used For */}
              <div className="flex items-start gap-3 mb-4" style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#f87171' }} />
                <div className="space-y-2">
                  <p className="font-semibold" style={{ color: '#e5e7eb', fontSize: '0.95rem' }}>🚫 What It Is Not Used For:</p>
                  <p className="italic font-semibold" style={{ color: 'white', fontSize: '0.92rem' }}>
                    The app Ascendra is not a substitute for professional medical care, mental health treatment,
                    or licensed therapy. It does not:
                  </p>
                  <ul className="list-none space-y-1.5" style={{ paddingLeft: 0 }}>
                    {[
                      'Diagnose, treat, or prevent any medical or psychological conditions',
                      'Provide crisis support or psychiatric care',
                      'Replace one-on-one clinical or therapeutic services',
                      'Ascendra does not provide emotional support, counseling, or guidance unrelated to food, eating, or behavioral patterns connected to the user\'s relationship with food.'
                    ].map((item, i) => (
                      <li key={i} className="relative" style={{ paddingLeft: '18px', fontSize: '0.92rem', color: 'white' }}>
                        <span className="absolute" style={{ left: '4px', top: 0, color: '#f87171', fontSize: '0.8rem' }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="font-semibold italic mt-2" style={{ color: 'white', fontSize: '0.92rem' }}>
                    If you are experiencing a medical emergency or mental health crisis, please contact a
                    licensed healthcare provider or emergency services in your area.
                  </p>
                </div>
              </div>

              {/* Data Privacy & Security */}
              <div className="flex items-start gap-3 mb-4" style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)'
              }}>
                <ShieldCheck size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#4ade80' }} />
                <div className="space-y-2">
                  <p className="font-semibold" style={{ color: '#e5e7eb', fontSize: '0.95rem' }}>🔐 Data Privacy & Security:</p>
                  <ul className="list-none space-y-1.5" style={{ paddingLeft: 0 }}>
                    {[
                      'Your data and conversations with Ascendra are private and confidential. Ascendra LLC does not sell, rent, or share your information with third parties. Any data collected is used solely to personalize your experience and support your progress within the AI system.',
                      'We are committed to upholding the highest standards of digital safety, emotional responsibility, and user integrity.'
                    ].map((item, i) => (
                      <li key={i} className="relative" style={{ paddingLeft: '18px', fontSize: '0.92rem', color: 'white'  }}>
                        <span className="absolute" style={{ left: '4px', top: 0, color: '#4ade80', fontSize: '0.8rem' }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Do Not Share Sensitive Information */}
              <div className="flex items-start gap-3 mb-4" style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(234, 179, 8, 0.1)',
                border: '1px solid rgba(234, 179, 8, 0.3)'
              }}>
                <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#facc15' }} />
                <div className="space-y-2">
                  <p className="font-semibold" style={{ color: '#e5e7eb', fontSize: '0.95rem' }}>⚠️ Do Not Share Sensitive Personal Information:</p>
                  <ul className="list-none space-y-1.5" style={{ paddingLeft: 0 }}>
                    <li className="relative" style={{ paddingLeft: '18px', fontSize: '0.92rem', color: 'white' }}>
                      <span className="absolute" style={{ left: '4px', top: 0, color: '#facc15', fontSize: '0.8rem' }}>•</span>
                      <span>For your own protection, please do not share sensitive personal details through the app. This app is not designed to collect or process such information under any circumstances.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Personal Responsibility */}
              <div className="flex items-start gap-3" style={{
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)'
              }}>
                <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#c084fc' }} />
                <div className="space-y-2">
                  <p className="font-semibold" style={{ color: '#e5e7eb', fontSize: '0.95rem' }}>⚖️ Personal Responsibility:</p>
                  <p style={{ fontSize: '0.92rem', color: 'white' }}>
                    All guidance provided by Ascendra is for educational and self-development purposes only.
                    Users are fully responsible for how they interpret and apply the app's prompts, insights,
                    or suggestions. Ascendra LLC is not liable for any decisions, actions, or consequences
                    resulting from the user's interpretation of or reliance on the app's content. Ascendra
                    does not provide emotional support or guidance outside the scope of food, eating, and
                    related behavioral patterns. Users must be over 18 years of age to use this application.
                  </p>
                </div>
              </div>
            </div>

            {/* Terms acceptance */}
            <div className="mb-5 border" style={{
              padding: '20px',
              borderRadius: '14px',
              background: 'rgba(15, 23, 42, 0.85)',
              borderColor: 'rgba(148, 163, 184, 0.35)'
            }}>
              <label className="flex items-start cursor-pointer group mb-5">
                <div className="relative flex items-center mt-0.5">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={handleCheckboxChange}
                    className="opacity-0 absolute h-5 w-5"
                  />
                  <div className={`border-2 rounded w-5 h-5 flex flex-shrink-0 justify-center items-center mr-3 transition-all ${accepted
                    ? "border-orange-500 bg-orange-500"
                    : "border-gray-500 group-hover:border-orange-400"
                    }`}>
                    {accepted && <Check size={14} className="text-white" />}
                  </div>
                </div>
                <span style={{ fontSize: '0.95rem', color: '#e5e7eb', lineHeight: 1.6 }}>
                  I confirm that I have read and understand the {" "}
                  <a href="/disclaimer" className="font-semibold transition-colors" style={{
                    color: '#fb923c',
                    textDecoration: 'none'
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#f97316'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#fb923c'}>
                    Disclaimer
                  </a>{" "}
                  and{" "}
                  <a href="/disclaimer" className="font-semibold transition-colors" style={{
                    color: '#fb923c',
                    textDecoration: 'none'
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#f97316'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#fb923c'}>
                    Privacy Policy
                  </a>
                  ,{' '}and I consent to proceed under these terms.
                </span>
              </label>

              {/* Button centered within the same section */}
              <div className="flex justify-center">
                <button
                  onClick={handleSubmit}
                  disabled={!accepted || loading}
                  className="flex items-center h-10 justify-center gap-2 rounded-full border-0 cursor-pointer transition-all w-full sm:w-auto"
                  style={{
                    padding: '14px 24px',
                    maxWidth: '320px',
                    background: accepted && !loading ? 'linear-gradient(135deg, #f97316, #fb923c)' : 'rgba(55, 65, 81, 0.5)',
                    color: accepted && !loading ? '#0b1120' : '#6b7280',
                    fontWeight: 600,
                    fontSize: '1rem',
                    boxShadow: accepted && !loading ? '0 16px 40px rgba(249, 115, 22, 0.55)' : 'none',
                    cursor: accepted && !loading ? 'pointer' : 'not-allowed',
                    transform: 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    if (accepted && !loading) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 20px 50px rgba(249, 115, 22, 0.6)';
                      e.currentTarget.style.opacity = '0.96';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (accepted && !loading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 16px 40px rgba(249, 115, 22, 0.55)';
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                >
                  <span>{loading ? "Processing..." : "Start My Journey"}</span>
                  {!loading && <ArrowRight size={18} />}
                  {loading && (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center mt-6" style={{
          fontSize: '0.75rem',
          color: '#6b7280'
        }}>
          © 2025 Ascendra LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default CoachDisclaimer;